import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { AIUsageTracker } from "../lib/ai/usage-tracker";

interface UsageData {
  monthly: {
    total_input_tokens: number;
    total_output_tokens: number;
    total_input_audio_seconds: number;
    total_output_audio_seconds: number;
    total_estimated_cost: number;
    total_sessions: number;
    token_quota: number;
    audio_quota_seconds: number;
    month: string;
  } | null;
  quota: {
    withinQuota: boolean;
    tokensRemaining: number;
    audioSecondsRemaining: number;
    percentUsed: number;
  } | null;
}

interface AIUsageContextType {
  tracker: AIUsageTracker | null;
  usageData: UsageData;
  refreshUsage: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AIUsageContext = createContext<AIUsageContextType | null>(null);

export function AIUsageProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn } = useAuth();
  const [tracker, setTracker] = useState<AIUsageTracker | null>(null);
  const [usageData, setUsageData] = useState<UsageData>({
    monthly: null,
    quota: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || "https://flowcus-api-prod.prizio.workers.dev";

  // Initialize tracker
  useEffect(() => {
    if (isSignedIn) {
      const newTracker = new AIUsageTracker(apiUrl, () => getToken({ template: "flowcus-api" }));
      setTracker(newTracker);
    }
  }, [isSignedIn, apiUrl, getToken]);

  // Fetch usage data
  const refreshUsage = useCallback(async () => {
    if (!tracker || !isSignedIn) return;

    setIsLoading(true);
    setError(null);

    try {
      const usage = await tracker.getCurrentMonthUsage();
      if (usage) {
        setUsageData(usage);
      }
    } catch (err) {
      console.error("Failed to fetch usage data:", err);
      setError("Failed to load usage data");
    } finally {
      setIsLoading(false);
    }
  }, [tracker, isSignedIn]);

  // Load initial usage data
  useEffect(() => {
    if (tracker && isSignedIn) {
      refreshUsage();
    }
  }, [tracker, isSignedIn, refreshUsage]);

  // Refresh usage periodically (every 60 seconds)
  useEffect(() => {
    if (!tracker || !isSignedIn) return;

    const interval = setInterval(() => {
      refreshUsage();
    }, 60000);

    return () => clearInterval(interval);
  }, [tracker, isSignedIn, refreshUsage]);

  return (
    <AIUsageContext.Provider
      value={{
        tracker,
        usageData,
        refreshUsage,
        isLoading,
        error,
      }}
    >
      {children}
    </AIUsageContext.Provider>
  );
}

export function useAIUsage() {
  const context = useContext(AIUsageContext);
  if (!context) {
    throw new Error("useAIUsage must be used within AIUsageProvider");
  }
  return context;
}
